import { useTranslation } from 'react-i18next';
import { ReviewPathology, BreastPathology, ProstatePathology, AgeValue, PathologyHistory, PathologyName } from '@myriadgenetics/mgh-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil, faTrash } from '@fortawesome/free-solid-svg-icons';
import { Text1, Text3 } from '../../../elements/typography';
import BreastCancerDetails from '../breast-cancer-details';
import ProstateCancerDetails from '../prostate-cancer-details';

import './index.scss';
import { isSimonMedUrl } from '../../../helpers/url';

const SEPARATOR = ':';

const ageValue = (ageResponse?: AgeValue): string => {
  if (Array.isArray(ageResponse)) {
    return ageResponse[0].optionValue;
  }
  if (ageResponse || ageResponse === 0) {
    return ageResponse.toString();
  }
  return 'N/A';
};

type Props = {
  pathology: ReviewPathology | PathologyHistory;
  onEditClicked?: ((pathology: ReviewPathology | PathologyHistory, index: number) => void) | undefined;
  onDeleteClicked?: (pathology: ReviewPathology | PathologyHistory, index: number) => void;
  pathologyNum: number;
};
function PathologyDetails({ pathology, onEditClicked, onDeleteClicked, pathologyNum }: Props) {
  const { t } = useTranslation();
  const { type } = pathology;
  const isProstateCancer = (type: PathologyName) => type === 'cancerHistory.cancers.prostate';
  const isFemaleBreastCancer = (type: PathologyName) => type === `cancerHistory.cancers.breastFemale` || type === `cancerHistory.cancers.dcis`;
  const baseClass = 'pathology-details';

  const relativeBilateralAge = (pathology: BreastPathology) => (
    <div>
      {pathology.first && <AgeDisplay age={ageValue(pathology.first.age)} labelKey="review.firstAge" />}
      {pathology.second && <AgeDisplay age={ageValue(pathology.second.age)} labelKey="review.secondAge" />}
    </div>
  );

  const bilateralAge = (pathology: BreastPathology) => (
    <div>
      {pathology.leftBreastAge && <AgeDisplay age={ageValue(pathology.leftBreastAge)} labelKey="review.leftAge" />}
      {pathology.rightBreastAge && <AgeDisplay age={ageValue(pathology.rightBreastAge)} labelKey="review.rightAge" />}
    </div>
  );

  const breastCancerAge = (pathology: BreastPathology) => {
    if (pathology.first && pathology.second) {
      return relativeBilateralAge(pathology);
    }
    if (pathology.left && pathology.right) {
      return bilateralAge(pathology);
    }
    return <AgeDisplay age={ageValue(pathology.age)} />;
  };

  const getDisplayedAge = (pathology: ReviewPathology | PathologyHistory) => {
    if (pathology.type === 'cancerHistory.cancers.breastFemale' || pathology.type === 'cancerHistory.cancers.dcis') {
      return breastCancerAge(pathology as BreastPathology);
    }
    return <AgeDisplay age={ageValue(pathology.age)} />;
  };

  return (
    <div className={baseClass}>
      <div className={`${baseClass}__type`}>
        <Text1 className="text-medium--semi-bold">{t(type)}</Text1>
        <div className={`${baseClass}__buttons`}>
          <button
            onClick={() => onEditClicked && onEditClicked(pathology as ReviewPathology, pathologyNum)}
            className={`${baseClass}__edit ${isSimonMedUrl() ? baseClass + '__edit--smi' : ''}`}
            data-testid="edit-pathology"
          >
            <FontAwesomeIcon icon={faPencil} />
          </button>
          <button
            onClick={() => onDeleteClicked && onDeleteClicked(pathology, pathologyNum)}
            className={`${baseClass}__del ${isSimonMedUrl() ? baseClass + '__edit--smi' : ''}`}
            data-testid="delete-pathology"
          >
            <FontAwesomeIcon icon={faTrash} />
          </button>
        </div>
      </div>
      {isProstateCancer(pathology.type) && <ProstateCancerDetails pathology={pathology as ProstatePathology} />}
      {isFemaleBreastCancer(pathology.type) && <BreastCancerDetails pathology={pathology as BreastPathology} />}
      {!isProstateCancer(pathology.type) && !isFemaleBreastCancer(pathology.type) && getDisplayedAge(pathology)}
    </div>
  );
}

type AgeDisplayProps = {
  age: string;
  labelKey?: string;
};

function AgeDisplay({ age, labelKey }: AgeDisplayProps) {
  const { t } = useTranslation();
  return (
    <p className="pathology-details__age-display">
      <Text3 className="pathology-details__age-aod text-medium--regular" tag="span">{`${t(labelKey || 'review.ageOfDiagnosis')}${SEPARATOR}`}</Text3>
      <Text3 className="pathology-details__age-text text-medium--semi-bold" tag="span" bold="true">
        {t(age)}
      </Text3>
    </p>
  );
}

export default PathologyDetails;
